import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getItens, deleteItem, reactivateItem, getCategoriasFlat, getFasesDeObra } from '../services/api';
import { Item, Categoria, FaseObra } from '../services/types';
import { getHierarchicalCategories } from '../utils/categoryUtils';
import {
    Typography,
    Box,
    Paper,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Alert,
    Chip,
    TextField,
    InputAdornment,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TablePagination,
    Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RestoreIcon from '@mui/icons-material/Restore';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ItemEditModal from '../components/ItemEditModal';
import ImportDocumentModal from '../components/ImportDocumentModal';

const ItensPage: React.FC = () => {
    const [itens, setItens] = useState<Item[]>([]);
    const [categorias, setCategorias] = useState<Categoria[]>([]);
    const [fases, setFases] = useState<FaseObra[]>([]);
    const [error, setError] = useState<string>('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState<Partial<Item> | null>(null);
    const [showInactive, setShowInactive] = useState(false);

    // Filters & Pagination
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategoriaId, setSelectedCategoriaId] = useState<string>('');
    const [selectedFaseId, setSelectedFaseId] = useState<string>('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    const fetchItens = useCallback(async () => {
        setError('');
        try {
            const [itensData, catsData, fasesData] = await Promise.all([
                getItens({ includeInactive: showInactive }),
                getCategoriasFlat(showInactive),
                getFasesDeObra(showInactive)
            ]);
            setItens(itensData || []);
            setCategorias(catsData || []);
            setFases(fasesData || []);
        } catch (err) {
            setError('Falha ao carregar itens do catálogo.');
            console.error(err);
        }
    }, [showInactive]);

    useEffect(() => {
        fetchItens();
    }, [fetchItens]);

    const hierarchicalCategorias = useMemo(() => getHierarchicalCategories(categorias), [categorias]);

    const filteredItens = useMemo(() => {
        const searchLower = searchTerm.trim().toLowerCase();

        // If a parent category is selected, include all its subcategories
        const matchingCategoryIds = new Set<number>();
        if (selectedCategoriaId) {
            const catIdNum = Number(selectedCategoriaId);
            matchingCategoryIds.add(catIdNum);
            categorias.forEach(c => {
                if (c.categoria_pai_id === catIdNum) {
                    matchingCategoryIds.add(c.id);
                }
            });
        }

        return itens.filter(item => {
            if (searchLower) {
                const descMatch = item.descricao.toLowerCase().includes(searchLower);
                const catMatch = (item.categoria_nome || '').toLowerCase().includes(searchLower);
                if (!descMatch && !catMatch) return false;
            }
            if (selectedCategoriaId && !matchingCategoryIds.has(item.categoria_id)) {
                return false;
            }
            if (selectedFaseId) {
                const faseIdNum = Number(selectedFaseId);
                const hasFase = item.fases && item.fases.some(f => f.id === faseIdNum);
                if (!hasFase) return false;
            }
            return true;
        });
    }, [itens, categorias, searchTerm, selectedCategoriaId, selectedFaseId]);

    const paginatedItens = useMemo(() => {
        const start = page * rowsPerPage;
        return filteredItens.slice(start, start + rowsPerPage);
    }, [filteredItens, page, rowsPerPage]);

    const handleOpenModal = (item: Partial<Item> | null) => {
        setSelectedItem(item);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedItem(null);
        fetchItens();
    };

    const handleDelete = async (id: number) => {
        if (window.confirm('Deseja desativar este item? Ele poderá ser reativado depois.')) {
            try {
                await deleteItem(id);
                fetchItens();
            } catch (err) {
                setError('Falha ao desativar o item.');
                console.error(err);
            }
        }
    };

    const handleReactivate = async (id: number) => {
        try {
            await reactivateItem(id);
            fetchItens();
        } catch (err) {
            setError('Falha ao reativar o item.');
            console.error(err);
        }
    };

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
    };

    return (
        <Box sx={{ p: 3 }}>
            <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                    <Box>
                        <Typography variant="h4">
                            Catálogo de Itens
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Exibindo {filteredItens.length} de {itens.length} itens cadastrados
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                            variant={showInactive ? 'contained' : 'outlined'}
                            size="small"
                            onClick={() => {
                                setShowInactive(!showInactive);
                                setPage(0);
                            }}
                            color="inherit"
                        >
                            {showInactive ? 'Ocultar Inativos' : 'Mostrar Inativos'}
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<UploadFileIcon />}
                            onClick={() => setIsImportModalOpen(true)}
                            color="primary"
                        >
                            Importar Documento
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => handleOpenModal(null)}
                        >
                            Adicionar Item
                        </Button>
                    </Box>
                </Box>

                {/* Barra de Busca e Filtros */}
                <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                    <TextField
                        size="small"
                        placeholder="Buscar por descrição ou categoria..."
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setPage(0);
                        }}
                        sx={{ flex: '1 1 280px' }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                            endAdornment: searchTerm ? (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => { setSearchTerm(''); setPage(0); }}>
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ) : null
                        }}
                    />

                    <FormControl size="small" sx={{ minWidth: 220 }}>
                        <InputLabel>Filtrar por Categoria</InputLabel>
                        <Select
                            value={selectedCategoriaId}
                            label="Filtrar por Categoria"
                            onChange={(e) => {
                                setSelectedCategoriaId(e.target.value);
                                setPage(0);
                            }}
                        >
                            <MenuItem value=""><em>Todas as Categorias</em></MenuItem>
                            {hierarchicalCategorias.map(cat => (
                                <MenuItem
                                    key={cat.id}
                                    value={String(cat.id)}
                                    sx={{ pl: (cat.level * 2) + 2, fontWeight: cat.level === 0 ? 'bold' : 'normal' }}
                                >
                                    {cat.level > 0 ? '— '.repeat(cat.level) : ''}{cat.nome}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 240 }}>
                        <InputLabel>Filtrar por Fase da Obra</InputLabel>
                        <Select
                            value={selectedFaseId}
                            label="Filtrar por Fase da Obra"
                            onChange={(e) => {
                                setSelectedFaseId(e.target.value);
                                setPage(0);
                            }}
                        >
                            <MenuItem value=""><em>Todas as Fases</em></MenuItem>
                            {fases.map(fase => (
                                <MenuItem key={fase.id} value={String(fase.id)}>
                                    {fase.nome}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {(searchTerm || selectedCategoriaId || selectedFaseId) && (
                        <Button
                            size="small"
                            variant="text"
                            onClick={() => {
                                setSearchTerm('');
                                setSelectedCategoriaId('');
                                setSelectedFaseId('');
                                setPage(0);
                            }}
                        >
                            Limpar Filtros
                        </Button>
                    )}
                </Box>

                {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'action.hover' }}>
                                <TableCell><strong>Descrição</strong></TableCell>
                                <TableCell><strong>Subcategoria</strong></TableCell>
                                <TableCell><strong>Fases da Obra</strong></TableCell>
                                <TableCell align="right"><strong>Valor Padrão</strong></TableCell>
                                <TableCell align="right"><strong>Ações</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedItens.map((item) => {
                                const catObj = hierarchicalCategorias.find(c => c.id === item.categoria_id);
                                const categoriaLabel = catObj?.parentName
                                    ? `${catObj.parentName} > ${catObj.nome}`
                                    : item.categoria_nome;

                                return (
                                    <TableRow key={item.id} sx={{ opacity: item.ativo ? 1 : 0.5 }} hover>
                                        <TableCell>
                                            {item.descricao}
                                            {!item.ativo && <Chip label="Inativo" size="small" color="default" sx={{ ml: 1 }} />}
                                        </TableCell>
                                        <TableCell>{categoriaLabel}</TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                {item.fases && item.fases.length > 0 ? (
                                                    item.fases.map((fase) => (
                                                        <Chip key={fase.id} label={fase.nome} size="small" variant="outlined" />
                                                    ))
                                                ) : (
                                                    <Chip label="Sem fase" size="small" color="warning" />
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right">{formatCurrency(item.valor_unitario_padrao)}</TableCell>
                                        <TableCell align="right">
                                            {item.ativo ? (
                                                <>
                                                    <Tooltip title="Editar Item">
                                                        <IconButton size="small" onClick={() => handleOpenModal(item)}>
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Desativar Item">
                                                        <IconButton size="small" color="error" onClick={() => handleDelete(item.id)}>
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </>
                                            ) : (
                                                <Tooltip title="Reativar Item">
                                                    <IconButton size="small" color="success" onClick={() => handleReactivate(item.id)}>
                                                        <RestoreIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {paginatedItens.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                        Nenhum item encontrado para os filtros aplicados.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    count={filteredItens.length}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    labelRowsPerPage="Itens por página:"
                    labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
                />

                <ItemEditModal
                    item={selectedItem}
                    open={isModalOpen}
                    onClose={handleCloseModal}
                />

                <ImportDocumentModal
                    open={isImportModalOpen}
                    onClose={() => setIsImportModalOpen(false)}
                    onSuccess={() => {
                        setIsImportModalOpen(false);
                        fetchItens();
                    }}
                />
            </Paper>
        </Box>
    );
};

export default ItensPage;
